class ProductSetCalculator extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      width: '[data-width]',
      height: '[data-height]',
      plinth: '[data-plinth]',
      warning: '[data-warning]',
      result: '[data-result]',
      footnote: '[data-footnote]',
    };

    this.onInput = this.onInput.bind(this);
  }

  connectedCallback() {
    this.widthInput = this.querySelector(this.selectors.width);
    this.heightInput = this.querySelector(this.selectors.height);
    this.plinthInput = this.querySelector(this.selectors.plinth);
    this.warningEl = this.querySelector(this.selectors.warning);
    this.warningTextEl = this.querySelector('[data-warning-text]');
    this.resultEl = this.querySelector(this.selectors.result);
    this.footnoteEl = this.querySelector(this.selectors.footnote);
    this.outputEl = this.querySelector('[data-output]');
    this.detailsEl = this.querySelector('.set-calc__details');
    this.priceRoot = this.querySelector('[data-price]');
    this.priceRegularEl = this.querySelector('.price-item--regular');
    this.priceSaleEl = this.querySelector('.price-item--sale');
    this.priceMetaEl = this.querySelector('[data-price-meta]');
    this.priceOriginalEl = this.querySelector('[data-price-original]');
    this.priceDiscountEl = this.querySelector('[data-price-discount]');
    this.priceDiscountTextEl = this.querySelector('[data-price-discount-text]');

    // Defensive: ensure badge structure exists even if stale theme markup is loaded.
    if (this.priceDiscountEl && !this.priceDiscountEl.querySelector('.triangle')) {
      const triangle = document.createElement('span');
      triangle.className = 'triangle';
      this.priceDiscountEl.prepend(triangle);
    }

    if (this.priceDiscountEl && !this.priceDiscountTextEl) {
      const textEl = document.createElement('span');
      textEl.setAttribute('data-price-discount-text', '');
      this.priceDiscountEl.append(textEl);
      this.priceDiscountTextEl = textEl;
    }

    this.panelWidthCm = this.toNumber(this.dataset.panelWidthCm);
    this.panelHeightCm = this.toNumber(this.dataset.panelHeightCm);
    this.panelsPerSet = this.toNumber(this.dataset.panelsPerSet);
    this.defaultPlinth = this.toNumber(this.dataset.defaultPlinth, 10);

    if (this.widthInput) this.widthInput.placeholder = this.dataset.i18nWidthPlaceholder || '';
    if (this.heightInput) this.heightInput.placeholder = this.dataset.i18nHeightPlaceholder || '';
    if (this.plinthInput) this.plinthInput.placeholder = this.dataset.i18nPlinthPlaceholder || '';

    if (this.plinthInput && !this.plinthInput.value) {
      this.plinthInput.value = String(this.defaultPlinth);
    }

    if (this.widthInput) this.widthInput.addEventListener('input', this.onInput);
    if (this.heightInput) this.heightInput.addEventListener('input', this.onInput);
    if (this.plinthInput) this.plinthInput.addEventListener('input', this.onInput);

    this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');
    this.handleDetailsMode = this.handleDetailsMode.bind(this);
    this.desktopMediaQuery.addEventListener('change', this.handleDetailsMode);
    this.handleDetailsMode(this.desktopMediaQuery);

    // Initial compute (in case fields are prefilled).
    this.compute();
  }

  disconnectedCallback() {
    if (this.widthInput) this.widthInput.removeEventListener('input', this.onInput);
    if (this.heightInput) this.heightInput.removeEventListener('input', this.onInput);
    if (this.plinthInput) this.plinthInput.removeEventListener('input', this.onInput);
    if (this.desktopMediaQuery) this.desktopMediaQuery.removeEventListener('change', this.handleDetailsMode);
  }

  onInput() {
    window.clearTimeout(this.inputTimer);
    this.inputTimer = window.setTimeout(() => this.compute(), 80);
  }

  compute() {
    const w = this.toNumber(this.widthInput?.value);
    const h = this.toNumber(this.heightInput?.value);
    const p = this.toNumber(this.plinthInput?.value);

    // Validation (PDF): stop if invalid.
    if (!w || !h || Number.isNaN(p)) {
      this.setHidden(this.warningEl, true);
      this.setHidden(this.resultEl, true);
      this.setHidden(this.footnoteEl, true);
      this.setHidden(this.outputEl, true);
      return;
    }

    if (!this.panelWidthCm || !this.panelHeightCm || !this.panelsPerSet) {
      this.setHidden(this.warningEl, true);
      this.setHidden(this.resultEl, true);
      this.setHidden(this.footnoteEl, true);
      this.setHidden(this.outputEl, true);
      return;
    }

    const availableHeight = h - p;
    const showWarning = availableHeight > this.panelHeightCm;
    const heightDelta = Math.max(0, availableHeight - this.panelHeightCm);

    const panels = Math.ceil(w / this.panelWidthCm);
    const sets = Math.ceil(panels / this.panelsPerSet);

    this.setHidden(this.warningEl, !showWarning);
    if (this.warningEl && this.warningTextEl && showWarning) {
      const cm = this.dataset.i18nCm || 'cm';
      const title = this.dataset.i18nWarningTitle || '';
      const before = this.dataset.i18nWarningBodyBefore || '';
      const after = this.dataset.i18nWarningBodyAfter || '';
      const contactLabel = this.dataset.i18nWarningContactLabel || '';
      const tail = this.dataset.i18nWarningTail || '';
      const contactUrl = this.dataset.contactUrl || '';
      const panelHeight = `${this.formatNumber(this.panelHeightCm)} ${cm}`;
      const deltaText = `${this.formatNumber(heightDelta)} ${cm}`;

      const contactLink = contactUrl
        ? `<a class="link" href="${this.escapeHtml(contactUrl)}">${this.escapeHtml(contactLabel)}</a>`
        : this.escapeHtml(contactLabel);

      this.warningTextEl.innerHTML = `
        <strong>${this.escapeHtml(title)}</strong>
        ${this.escapeHtml(before)} <strong>${this.escapeHtml(panelHeight)}</strong> ${this.escapeHtml(after)} <strong>${this.escapeHtml(deltaText)}</strong> hoger.
        Neem even ${contactLink} ${this.escapeHtml(tail)}.
      `.trim();
    }

    if (this.resultEl) {
      if (this.panelsPerSet === 1) {
        const template =
          panels === 1 ? this.dataset.i18nResultPanelsOnlyOne : this.dataset.i18nResultPanelsOnlyOther;
        this.resultEl.textContent = this.interpolateCountTemplate(template, panels);
      } else {
        const setTemplate = sets === 1 ? this.dataset.i18nSetsOne : this.dataset.i18nSetsOther;
        const panelTemplate = panels === 1 ? this.dataset.i18nPanelsOne : this.dataset.i18nPanelsOther;
        const setWord = this.interpolateCountTemplate(setTemplate, sets);
        const panelWord = this.interpolateCountTemplate(panelTemplate, panels);

        const resultPrefixTemplate = this.dataset.i18nResultPrefix || '';
        const resultSuffixTemplate = this.dataset.i18nResultSuffix || '';
        const resultPrefix = this.interpolateNumberTemplate(resultPrefixTemplate, sets);
        const resultSuffix = this.interpolateNumberTemplate(resultSuffixTemplate, panels);

        this.resultEl.textContent = `${resultPrefix}${setWord}${resultSuffix}${panelWord})`;
      }
    }

    const footnotePrefix = this.dataset.i18nFootnotePrefix || '';
    const footnoteSuffix = this.dataset.i18nFootnoteSuffix || '';
    const footnoteSuffixSingle = this.dataset.i18nFootnoteSuffixSingle || '';
    const cm = this.dataset.i18nCm || 'cm';

    if (this.footnoteEl) {
      const dims = `${this.formatNumber(this.panelWidthCm)} × ${this.formatNumber(this.panelHeightCm)} ${cm}`;
      if (this.panelsPerSet === 1) {
        this.footnoteEl.textContent = `${footnotePrefix} ${dims}. ${footnoteSuffixSingle}`;
      } else {
        const panelsPerSetTemplate = this.panelsPerSet === 1 ? this.dataset.i18nPanelsOne : this.dataset.i18nPanelsOther;
        const panelsPerSetText = this.interpolateCountTemplate(panelsPerSetTemplate, this.panelsPerSet);
        this.footnoteEl.textContent = `${footnotePrefix} ${dims}. ${footnoteSuffix} ${panelsPerSetText}.`;
      }
    }

    this.setHidden(this.resultEl, false);
    this.setHidden(this.footnoteEl, false);
    this.setHidden(this.outputEl, false);

    this.updateCalculatedPrice(sets);
    this.syncQuantity(sets);
  }

  setHidden(el, hidden) {
    if (!el) return;
    el.hidden = Boolean(hidden);
  }

  toNumber(value, fallback = NaN) {
    const n = Number.parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isNaN(n) ? fallback : n;
  }

  formatNumber(value) {
    const n = this.toNumber(value);
    if (Number.isNaN(n)) return '';
    return Number.isInteger(n) ? String(n) : String(n).replace(/0+$/, '').replace(/\.$/, '');
  }

  syncQuantity(quantity) {
    const root = this.closest('product-info') || document;
    const quantityInput = root.querySelector('[name="quantity"]');
    if (!quantityInput) return;

    const next = quantity === null ? 1 : Math.max(1, Math.round(quantity));
    quantityInput.value = String(next);
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    const select = root.querySelector('select[name^="select-quantity"]');
    if (select) {
      select.value = String(next);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateCalculatedPrice(quantity) {
    if (!this.priceRoot || !window.Currency?.formatMoney || !window.currency?.currencyFormat) return;

    // The calculator block may not be nested inside `product-info`.
    // We still need the same discount table + main price scope the theme uses.
    const productInfo = this.closest('product-info') || document.querySelector('product-info');
    const sectionId = productInfo?.dataset?.originalSection || productInfo?.dataset?.section;
    const mainPriceScope = sectionId ? productInfo?.querySelector(`#price-${sectionId}`) : null;
    const mainPriceData = mainPriceScope?.querySelector('[data-price]');

    // Base per-unit price comes from the underlying price element.
    // Exemptify uses text nodes and its own clone markup; we should not
    // fight it by overwriting its injected nodes here.
    const basePrice = Number.parseInt(mainPriceData?.dataset?.price || this.priceRoot.dataset.price, 10);
    if (Number.isNaN(basePrice)) return;

    let discountedPrice = basePrice;
    let discountPercentage = 0;
    let applicableQuantity = null;
    const quantityTable =
      productInfo?.querySelector('table-quantity') || document.querySelector('table-quantity');
    const discountScript = quantityTable?.querySelector('script[type="application/json"]');

    if (discountScript?.textContent) {
      try {
        const discountData = JSON.parse(discountScript.textContent);
        const sortedQuantities = Object.keys(discountData)
          .map((qty) => Number.parseInt(qty, 10))
          .sort((a, b) => b - a);
        applicableQuantity = sortedQuantities.find((qty) => quantity >= qty);

        if (applicableQuantity) {
          discountPercentage = Number.parseFloat(discountData[applicableQuantity]);
          const discountAmount = basePrice * (discountPercentage / 100);
          discountedPrice = basePrice - discountAmount;
        }
      } catch (error) {
        console.error('Error parsing calculator discount data:', error);
      }
    }

    const totalPrice = discountedPrice * quantity;
    const baseTotal = basePrice * quantity;
    const formatted = window.Currency.formatMoney(totalPrice, window.currency.currencyFormat);

    if (this.priceRegularEl) this.priceRegularEl.innerHTML = formatted;
    if (this.priceSaleEl) this.priceSaleEl.innerHTML = formatted;

    if (!this.priceMetaEl || !this.priceOriginalEl || !this.priceDiscountEl || !this.priceDiscountTextEl) return;

    if (discountPercentage > 0 && quantity > 1) {
      this.priceOriginalEl.innerHTML = window.Currency.formatMoney(baseTotal, window.currency.currencyFormat);

      const discountTemplate = this.dataset.i18nDiscountLabel || '__count__%';
      const discountText = String(discountTemplate).replace('__count__', this.formatNumber(discountPercentage));
      this.priceDiscountTextEl.textContent = discountText;

      this.setHidden(this.priceMetaEl, false);
    } else {
      this.priceOriginalEl.textContent = '';
      this.priceDiscountTextEl.textContent = '';
      this.setHidden(this.priceMetaEl, true);
    }

  }

  escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  interpolateNumberTemplate(template, number) {
    return String(template).replace('__number__', this.formatNumber(number));
  }

  interpolateCountTemplate(template, count) {
    return String(template).replace(/\b(1|2)\b/, this.formatNumber(count));
  }

}

if (!customElements.get('product-set-calculator')) {
  customElements.define('product-set-calculator', ProductSetCalculator);
}

